import { EXPLORE_IMAGES } from '../exploreImages';
import { OFFICIAL_SOURCES, LAST_VERIFIED, VISA_FEES_USD } from '../officialSources';
import { mergeStepGuided } from './sharedJourneyGuided';
import { createJourney } from './journeyEngine';
import {
	getPermitDetails, getHeritageFees, formatPermitLines, totalPermitBudgetUsd,
	PERMIT_OFFICES,
} from './permitRegistry';
import {
	getWeatherForTrek, getPackingForTrek, getTransportForAccess,
} from './regionData';

const ACCESS_LABELS = {
	lukla: { stepName: 'Lukla Flight', icon: '🛫', question: 'Kathmandu → Lukla domestic flight' },
	road: { stepName: 'Trailhead', icon: '🚐', question: 'Road access to trailhead' },
	pokhara: { stepName: 'Trailhead', icon: '🚐', question: 'Pokhara to trailhead' },
	flight: { stepName: 'Domestic flight', icon: '🛫', question: 'Domestic flight to trailhead region' },
	heritage: { stepName: 'Start point', icon: '📍', question: 'Where your journey begins' },
	safari: { stepName: 'Park entry', icon: '🌿', question: 'Getting to the national park' },
};

function heroFor(imageKey) {
	if (imageKey === 'annapurnaAbc') return EXPLORE_IMAGES.annapurna;
	if (imageKey === 'peacePagoda') return EXPLORE_IMAGES.valley;
	if (imageKey === 'himalayanVillage') return EXPLORE_IMAGES.wildlife;
	if (imageKey === 'janakiMandir') return EXPLORE_IMAGES.janakiMandir;
	return EXPLORE_IMAGES.trekking;
}

function getJourneyKind(trek) {
	if (trek.access === 'heritage') return 'heritage';
	if (trek.access === 'safari') return 'safari';
	return 'trek';
}

function buildPermitSteps(trek) {
	const kind = getJourneyKind(trek);
	const heritage = getHeritageFees(trek.slug);
	const details = getPermitDetails(trek.permit);

	if (kind === 'heritage' && heritage) {
		const siteList = heritage.sites.map((s) => `${s.name}: NPR ${s.feeForeignNpr}${s.feeNote ? ` (${s.feeNote})` : ''}`).join('; ');
		return [
			{
				id: 'permits',
				name: 'Entry fees',
				icon: '🎫',
				question: 'Site access — not trekking permits',
				lead: heritage.summary,
				sections: [{
					id: 'permits',
					question: 'What you need',
					answer: `No national park or restricted-area trekking permit. ${heritage.notes}`,
					source: heritage.source,
				}],
				nextLabel: 'Site tickets →',
			},
			{
				id: 'permit-offices',
				name: 'Site tickets',
				icon: '🏛',
				question: 'Pay entrance fees at each site',
				lead: `Budget ~NPR ${heritage.totalBudgetNpr.toLocaleString()} for main sites`,
				sections: [{
					id: 'where',
					question: 'Site fees (foreigners)',
					answer: siteList,
				}],
				nextLabel: 'Guide →',
			},
		];
	}

	if (kind === 'safari') {
		const feeText = formatPermitLines(details);
		return [
			{
				id: 'permits',
				name: 'Park entry',
				icon: '🌿',
				question: 'National park entry permit',
				lead: details.summary,
				sections: [{ id: 'permits', question: 'Park fees', answer: feeText, source: details.source }],
				nextLabel: 'Park entry →',
			},
			{
				id: 'permit-offices',
				name: 'Park entry',
				icon: '📍',
				question: 'Where to pay park fees',
				lead: details.lines[0]?.where || 'Via your lodge or park gate',
				sections: [{
					id: 'where',
					question: 'How to pay',
					answer: feeText,
				}],
				nextLabel: 'Guide →',
			},
		];
	}

	if (trek.permit === 'none') {
		return [
			{
				id: 'permits',
				name: 'Permits',
				icon: '📋',
				question: 'Permit check for this route',
				lead: details.summary,
				sections: [{
					id: 'permits',
					question: 'Permits',
					answer: `${details.summary}. Confirm locally if your exact route crosses a conservation area boundary.`,
					source: details.source,
				}],
				nextLabel: 'Confirm locally →',
			},
			{
				id: 'permit-offices',
				name: 'Permit check',
				icon: '📍',
				question: 'Do you need a permit office visit?',
				lead: 'Usually not for this route',
				sections: [{
					id: 'where',
					question: 'When to visit NTB',
					answer: 'Most low-altitude routes with no park entry do not require a Kathmandu permit office visit. If your itinerary enters a national park or conservation area, verify at NTB Bhrikutimandap before you travel.',
				}],
				nextLabel: 'Guide →',
			},
		];
	}

	const feeText = formatPermitLines(details);
	const timsNote = details.timsRequired ? '' : ' TIMS card not required for this route (discontinued 2023).';
	return [
		{
			id: 'permits',
			name: 'Permits',
			icon: '📋',
			question: 'Required permits',
			lead: details.summary,
			sections: [{ id: 'permits', question: 'Permits & fees', answer: `${feeText}${timsNote}`, source: details.source }],
			nextLabel: 'Permit offices →',
		},
		{
			id: 'permit-offices',
			name: 'Permit Offices',
			icon: '📍',
			question: 'Where to obtain permits',
			lead: details.offices[0]?.name || 'Kathmandu permit counters',
			sections: [{
				id: 'where',
				question: 'Offices & documents',
				answer: `${feeText} Bring: ${details.documents.join(', ')}.`,
			}],
			nextLabel: 'Guide →',
		},
	];
}

function buildSteps(trek) {
	const details = getPermitDetails(trek.permit);
	const access = ACCESS_LABELS[trek.access] || ACCESS_LABELS.road;
	const kind = getJourneyKind(trek);
	const transport = getTransportForAccess(trek.access, trek.trailhead);
	const guideNote = trek.restricted
		? 'Licensed guide is mandatory for this route.'
		: kind === 'heritage'
			? 'A local cultural guide is optional but helps with temple etiquette and navigation.'
			: 'Independent trekking is commonly done — verify current NTB policy.';

	const insuranceLead = kind === 'heritage' || kind === 'safari'
		? 'Standard travel medical insurance for your trip.'
		: `Altitude cover to ${trek.insuranceAltitude}m minimum.`;
	const insuranceAnswer = kind === 'heritage' || kind === 'safari'
		? 'Policy should cover medical treatment and emergency evacuation in Nepal. High-altitude trekking cover is not required for this route.'
		: `Policy must cover helicopter evacuation and trekking to ${trek.insuranceAltitude}m.`;

	const packingLead = kind === 'heritage'
		? 'Comfortable walking shoes, modest temple dress, day bag.'
		: kind === 'safari'
			? 'Light layers, insect repellent, neutral colours for jungle drives.'
			: 'Use the packing panel for this journey.';
	const packingAnswer = kind === 'heritage'
		? 'Comfortable shoes, modest clothing for temples (cover shoulders and knees), sun hat, reusable water bottle, NPR cash for site tickets, light rain layer.'
		: kind === 'safari'
			? 'Neutral-coloured clothing, insect repellent, sun hat, binoculars, light jacket for early-morning safaris.'
			: 'Boots, layers, rain shell, sleeping bag rated for trail temperatures, purification, first aid.';

	const kathmanduLead = kind === 'heritage'
		? 'SIM, NPR cash, and site tickets before you start walking.'
		: kind === 'safari'
			? 'SIM, cash, and lodge booking before heading to the park.'
			: 'SIM, cash, gear, permits before heading to trail.';
	const kathmanduAnswer = kind === 'heritage'
		? `Withdraw NPR for site entrance fees. Start at ${trek.trailhead}. A local guide can be booked in Thamel or at major sites.`
		: kind === 'safari'
			? `Book your jungle lodge in advance. Travel by road or flight to ${trek.trailhead}. Park entry is usually arranged by the lodge.`
			: `Allow time in Kathmandu for permits and gear. Trailhead: ${trek.trailhead}.`;

	const trekStep = kind === 'heritage'
		? {
			id: 'trek',
			name: 'Explore',
			icon: '🚶',
			question: 'On the route',
			lead: `${trek.duration} — ${trek.title}`,
			isTrekDays: true,
			sections: [{ id: 'route', question: 'Overview', answer: `${trek.title} across ${trek.district}. Pace yourself — allow time at each site. ${trek.highlight}` }],
			nextLabel: 'Emergency →',
		}
		: kind === 'safari'
			? {
				id: 'trek',
				name: 'Safari',
				icon: '🐘',
				question: 'In the park',
				lead: `${trek.duration} — ${trek.title}`,
				isTrekDays: true,
				sections: [{ id: 'route', question: 'Overview', answer: `${trek.title} in ${trek.district}. Jeep and walking safaris with your lodge guide. ${trek.highlight}` }],
				nextLabel: 'Emergency →',
			}
			: {
				id: 'trek',
				name: 'Trek',
				icon: '🥾',
				question: 'On the trail',
				lead: `${trek.duration} — ${trek.title}`,
				isTrekDays: true,
				sections: [{ id: 'route', question: 'Overview', answer: `${trek.title} in ${trek.region}. Max altitude ${trek.maxAltitude}. ${trek.highlight}` }],
				nextLabel: 'Emergency →',
			};

	const emergencyAnswer = kind === 'heritage' || kind === 'safari'
		? 'Tourist Police 1144 (toll-free, English). Save your insurer and hotel numbers offline. For medical issues, major cities have hospitals; safari lodges have first-aid access.'
		: 'Tourist Police 1144 (toll-free, English). Descend if AMS worsens. Helicopter evacuation via insurer.';

	return [
		{
			id: 'dream',
			name: 'Dream',
			icon: '💭',
			question: `Is ${trek.title} right for me?`,
			lead: trek.highlight,
			sections: [{ id: 'overview', question: 'Why this trek?', answer: trek.highlight }],
			nextLabel: 'Research →',
		},
		{
			id: 'research',
			name: 'Research',
			icon: '🔍',
			question: 'Season, duration, budget?',
			lead: `${trek.duration} · ${trek.difficulty} · ${trek.budget}`,
			sections: [
				{ id: 'facts', question: 'Key facts', answer: `Duration: ${trek.duration}. Max altitude: ${trek.maxAltitude}. Best seasons: ${trek.seasons}. Budget: ${trek.budget} (excluding international flights).`, table: { headers: ['Detail', 'Value'], rows: [['Difficulty', trek.difficulty], ['District', trek.district], ['Region', trek.region]] } },
			],
			nextLabel: 'Visa →',
		},
		{
			id: 'visa',
			name: 'Visa',
			icon: '🛂',
			question: 'Nepal tourist visa',
			lead: '30-day tourist visa — USD 50 (2026 rate).',
			sections: [{ id: 'visa', question: 'Requirements', answer: `Tourist visa required. USD ${VISA_FEES_USD[30]} for 30 days. USD ${VISA_FEES_USD[15]} for 15 days. Apply online at nepaliport.immigration.gov.np or on arrival at TIA.`, source: OFFICIAL_SOURCES.immigration }],
			nextLabel: 'Flights →',
		},
		{
			id: 'flights',
			name: 'Flights',
			icon: '✈️',
			question: 'Getting to Nepal and the trail',
			lead: trek.access === 'lukla' ? 'International to KTM, then Lukla flight.' : trek.access === 'pokhara' ? 'International to KTM, then Pokhara.' : trek.access === 'heritage' ? 'International flight to Kathmandu.' : trek.access === 'safari' ? 'International to KTM, then road/flight to park.' : 'International flights to Kathmandu.',
			sections: [{ id: 'access', question: 'Transport', answer: `Fly to Kathmandu (TIA). ${transport.summary} ${transport.trailhead}${transport.roadAlternative ? ` Alternative: ${transport.roadAlternative}` : ''}` }],
			nextLabel: 'Insurance →',
		},
		{
			id: 'insurance',
			name: 'Insurance',
			icon: '🛡',
			question: 'Travel insurance',
			lead: insuranceLead,
			sections: [{ id: 'cover', question: 'Coverage', answer: insuranceAnswer }],
			nextLabel: 'Packing →',
		},
		{
			id: 'packing',
			name: 'Packing',
			icon: '🎒',
			question: 'Gear checklist',
			lead: packingLead,
			sections: [{ id: 'gear', question: 'Essentials', answer: packingAnswer }],
			nextLabel: 'Permits →',
		},
		...buildPermitSteps(trek),
		{
			id: 'guide',
			name: 'Guide',
			icon: '🧭',
			question: kind === 'heritage' ? 'Cultural guide (optional)' : kind === 'safari' ? 'Safari guide' : 'Guide requirements',
			lead: guideNote,
			sections: [{ id: 'guide', question: 'Guide rules', answer: guideNote, source: OFFICIAL_SOURCES.ntb }],
			nextLabel: 'Agencies →',
		},
		{
			id: 'sherpa',
			name: kind === 'heritage' ? 'Local guide' : kind === 'safari' ? 'Lodge booking' : 'Sherpa',
			icon: kind === 'heritage' ? '🗺' : kind === 'safari' ? '🏨' : '🏔',
			question: kind === 'heritage' ? 'Book a cultural guide' : kind === 'safari' ? 'Book your jungle lodge' : 'Licensed agencies',
			lead: kind === 'heritage' ? 'Licensed city guides available in Thamel and at major sites.' : kind === 'safari' ? 'Book a TAAN-registered lodge with safari package.' : 'Book through TAAN-registered agencies.',
			sections: [{ id: 'agency', question: kind === 'safari' ? 'Lodge' : 'Hiring', answer: kind === 'heritage' ? 'Nepal Tourism Board lists licensed guides. Expect USD 25–40/day for a private cultural guide in the valley.' : kind === 'safari' ? 'Book lodge packages including meals, safaris, and park entry. Chitwan and Bardia lodges are bookable online or via Kathmandu agencies.' : 'Use TAAN-registered trekking agencies in Thamel or Pokhara. Avoid airport touts.' }],
			nextLabel: 'Porter →',
		},
		{
			id: 'porter',
			name: 'Porter',
			icon: '📦',
			question: kind === 'heritage' ? 'Day bag only' : kind === 'safari' ? 'Luggage to lodge' : 'Porter logistics',
			lead: kind === 'heritage' ? 'Carry a light daypack — no porter needed.' : kind === 'safari' ? 'Lodge staff handle bags — pack light for jungle.' : 'Optional — carries duffel while you use a daypack.',
			sections: [{ id: 'porter', question: 'Details', answer: kind === 'heritage' ? 'A 15–20L daypack is enough for water, tickets, and camera. Leave large luggage at your hotel.' : kind === 'safari' ? 'Soft bag recommended. Most lodges are a short walk from the road.' : 'Typical cost USD 15–22/day. Max 15 kg per porter.' }],
			nextLabel: 'Kathmandu prep →',
		},
		{
			id: 'kathmandu',
			name: 'Kathmandu',
			icon: '🏙',
			question: kind === 'heritage' ? 'Pre-walk preparation' : kind === 'safari' ? 'Pre-safari preparation' : 'Pre-trek preparation',
			lead: kathmanduLead,
			sections: [{ id: 'prep', question: 'Prep', answer: kathmanduAnswer }],
			nextLabel: `${access.stepName} →`,
		},
		{
			id: 'lukla',
			name: access.stepName,
			icon: access.icon,
			question: access.question,
			lead: `Trailhead: ${trek.trailhead}`,
			sections: [{ id: 'trailhead', question: 'Access', answer: `Start point: ${trek.trailhead}. ${trek.access === 'lukla' ? 'Weather delays routine — buffer days in KTM.' : ''}` }],
			nextLabel: 'Trek →',
		},
		trekStep,
		{
			id: 'emergency',
			name: 'Emergency',
			icon: '🚨',
			question: 'Emergency planning',
			lead: 'Tourist Police 1144 · insurer emergency line',
			sections: [{ id: 'emergency', question: 'Contacts', answer: emergencyAnswer }],
			nextLabel: 'Return →',
		},
		{
			id: 'return',
			name: 'Return',
			icon: '🏠',
			question: 'Heading home',
			lead: 'Buffer days before international departure.',
			sections: [{ id: 'return', question: 'Return', answer: 'Return to Kathmandu, allow 1–2 buffer days before international flight.' }],
		},
	];
}

function buildTrekDays(trek) {
	const kind = getJourneyKind(trek);

	if (kind === 'heritage') {
		if (trek.slug === 'janakpur-pilgrimage') {
			return [
				{ day: 1, title: 'Arrive Janakpur — Janaki Mandir', sleep: 'Janakpur (100m)', altitude: trek.maxAltitude, distance: 'City walk', time: 'Half day', food: 'Terai thali & sweets', medical: 'Janakpur hospital', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: 'Dress modestly for temple. Evening aarti at Janaki Mandir.' },
				{ day: 2, title: 'Mithila art & sacred ponds', sleep: 'Janakpur', altitude: trek.maxAltitude, distance: '5–8 km', time: 'Full day', food: 'Local restaurants', medical: 'Clinics nearby', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: 'Mithila painting workshops, Ganga Sagar and Dhanush Sagar ponds.' },
				{ day: 3, title: 'Villages or departure', sleep: 'Janakpur / onward', altitude: trek.maxAltitude, distance: 'Flexible', time: 'Half day', food: 'Local', medical: 'Clinics nearby', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: trek.highlight },
			];
		}
		if (trek.slug === 'lumbini-pilgrimage') {
			return [
				{ day: 1, title: 'Arrive Lumbini — Maya Devi Temple', sleep: 'Lumbini (150m)', altitude: trek.maxAltitude, distance: 'Garden walk', time: 'Half day', food: 'Monastic zone cafés', medical: 'Lumbini clinic', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: 'Maya Devi Temple and Ashoka Pillar — birthplace of the Buddha.' },
				{ day: 2, title: 'Monastic zone circuit', sleep: 'Lumbini', altitude: trek.maxAltitude, distance: '6–10 km', time: 'Full day', food: 'Local / lodge', medical: 'Clinic nearby', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: 'Walk between international monasteries — rent a bicycle at the gate.' },
				{ day: 3, title: 'World Peace Pagoda & departure', sleep: 'Lumbini / onward', altitude: trek.maxAltitude, distance: 'Flexible', time: 'Half day', food: 'Local', medical: 'Clinic nearby', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: trek.highlight },
			];
		}
		if (trek.slug === 'kathmandu-heritage') {
			return [
				{ day: 1, title: 'Kathmandu Durbar Square & Swayambhunath', sleep: 'Kathmandu', altitude: trek.maxAltitude, distance: '5–8 km walking', time: 'Full day', food: 'Cafés & local', medical: 'Hospitals in valley', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: 'Buy site tickets at the gate. Dress modestly for temples.' },
				{ day: 2, title: 'Patan & Boudhanath', sleep: 'Patan / Kathmandu', altitude: trek.maxAltitude, distance: '5–10 km walking', time: 'Full day', food: 'Cafés & local', medical: 'Clinics nearby', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: 'Allow time at each UNESCO site — photography rules vary.' },
				{ day: 3, title: 'Bhaktapur or extension day', sleep: 'Bhaktapur / return', altitude: trek.maxAltitude, distance: 'Flexible', time: 'Full day', food: 'Local restaurants', medical: 'Clinics nearby', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: trek.highlight },
			];
		}
		const site = trek.trailhead.split('/')[0].trim();
		return [
			{ day: 1, title: `Arrive — ${site}`, sleep: site, altitude: trek.maxAltitude, distance: 'City walk', time: 'Half day', food: 'Local restaurants', medical: 'Clinics nearby', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: trek.highlight },
			{ day: 2, title: `${trek.title} — main sites`, sleep: site, altitude: trek.maxAltitude, distance: 'Flexible', time: 'Full day', food: 'Local', medical: 'Clinics nearby', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: 'Dress modestly for sacred sites.' },
			{ day: 3, title: 'Extension or departure', sleep: `${site} / onward`, altitude: trek.maxAltitude, distance: 'Flexible', time: 'Half day', food: 'Local', medical: 'Clinics nearby', atm: 'Yes', signal: 'Strong', electricity: 'Yes', water: 'Yes', notes: trek.highlight },
		];
	}

	if (trek.slug === 'muktinath-pilgrimage') {
		return [
			{ day: 1, title: 'Fly to Jomsom', sleep: 'Jomsom (2,720m)', altitude: '2,720m', distance: 'Flight day', time: 'Travel day', food: 'Teahouse', medical: 'Basic clinic', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: 'Acclimatize after flight — do not rush altitude.' },
			{ day: 2, title: 'Jomsom → Kagbeni', sleep: 'Kagbeni (2,800m)', altitude: '2,800m', distance: '10–12 km', time: '4–5 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Yes', notes: 'Kali Gandaki gorge — windy afternoons.' },
			{ day: 3, title: 'Kagbeni → Muktinath temple', sleep: 'Muktinath (3,800m)', altitude: trek.maxAltitude, distance: '8–10 km', time: '4–6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Yes', notes: '108 waterspouts and eternal flame — sacred to Hindus and Buddhists.' },
			{ day: 4, title: 'Return to Jomsom / fly out', sleep: 'Jomsom / Kathmandu', altitude: 'Descending', distance: 'Trek or jeep', time: 'Full day', food: 'Teahouse', medical: 'Basic clinic', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: trek.highlight },
		];
	}

	if (kind === 'safari') {
		const park = trek.title.replace(' Journey', '').replace(' Safari', '');
		return [
			{ day: 1, title: `Arrive — ${trek.trailhead}`, sleep: 'Jungle lodge', altitude: trek.maxAltitude, distance: 'Travel day', time: 'Half day', food: 'Lodge meals', medical: 'Lodge first aid', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: `${park} — park entry usually arranged by your lodge.` },
			{ day: 2, title: 'Jeep & walking safari', sleep: 'Jungle lodge', altitude: trek.maxAltitude, distance: 'Park drives', time: 'Dawn + dusk', food: 'Lodge meals', medical: 'Lodge first aid', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Yes', notes: trek.highlight },
			{ day: 3, title: 'Final safari & departure', sleep: 'Lodge / onward', altitude: trek.maxAltitude, distance: 'Flexible', time: 'Half day', food: 'Lodge meals', medical: 'Lodge first aid', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: 'Neutral clothing, insect repellent, follow guide instructions.' },
		];
	}

	return [
		{ day: 1, title: `Start — ${trek.trailhead}`, sleep: trek.trailhead, altitude: 'Trail start', distance: '—', time: 'Travel day', food: 'Teahouse/lodge', medical: 'Basic', atm: 'Varies', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: `Begin ${trek.title}. Pace yourself on day one.` },
		{ day: 2, title: `On trail — ${trek.district}`, sleep: 'Teahouse / camp', altitude: 'Building', distance: '8–12 km', time: '5–7 hrs', food: 'Teahouse', medical: 'Limited', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', notes: 'Steady pace — do not rush altitude gain.' },
		{ day: 3, title: `Highlight — ${trek.maxAltitude}`, sleep: 'Teahouse / camp', altitude: trek.maxAltitude, distance: '6–10 km', time: '5–8 hrs', food: 'Teahouse', medical: 'Limited', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', notes: trek.highlight },
		{ day: 4, title: 'Return leg', sleep: 'Lower elevation', altitude: 'Descending', distance: '10–15 km', time: '5–7 hrs', food: 'Teahouse', medical: 'Limited', atm: 'No', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: 'Descent — knee care on long downhills.' },
	];
}

function buildGuidedMeta(trek) {
	const details = getPermitDetails(trek.permit);
	const heritage = getHeritageFees(trek.slug);
	const kind = getJourneyKind(trek);
	const budgetMid = trek.budget.match(/\$[\d,]+/g);
	const mid = budgetMid ? parseInt(budgetMid[0].replace(/[$,]/g, ''), 10) : 1000;
	const permitBudget = kind === 'heritage' && heritage
		? Math.round(heritage.totalBudgetNpr / 135)
		: totalPermitBudgetUsd(trek.permit);

	const permitGuided = kind === 'heritage'
		? {
			objective: 'Understand site entrance fees (no trekking permit)',
			why: 'Heritage sites charge local gate fees — not NTB trekking permit offices',
			estimatedTime: '15 minutes',
			prerequisites: ['flights'],
			taskChecklist: [{ id: 'understood', label: 'I know which sites charge entry fees' }],
		}
		: kind === 'safari'
			? {
				objective: `Arrange ${details.summary}`,
				why: formatPermitLines(details),
				estimatedTime: '20 minutes',
				prerequisites: ['flights'],
				taskChecklist: [{ id: 'understood', label: 'Park entry arranged via lodge or gate' }],
			}
			: trek.permit === 'none'
				? {
					objective: 'Confirm no trekking permit is needed',
					why: details.summary,
					estimatedTime: '15 minutes',
					prerequisites: ['flights'],
					taskChecklist: [{ id: 'understood', label: 'Permit requirements confirmed for my route' }],
				}
				: {
					objective: `Obtain ${details.summary}`,
					why: formatPermitLines(details),
					estimatedTime: '30 minutes',
					prerequisites: ['flights'],
					taskChecklist: [{ id: 'understood', label: 'Permit requirements understood' }],
					documentsRequired: details.documents,
				};

	const permitOfficeGuided = kind === 'heritage'
		? {
			objective: 'Budget NPR cash for site tickets',
			why: 'Each durbar square, stupa, and temple may charge a separate on-site entry fee',
			estimatedTime: '20 minutes',
			prerequisites: ['permits'],
			taskChecklist: [{ id: 'obtained', label: 'NPR cash ready for site entrance fees' }],
		}
		: kind === 'safari'
			? {
				objective: 'Confirm park entry with your lodge',
				why: 'Jungle lodges usually bundle park fees into your package',
				estimatedTime: '20 minutes',
				prerequisites: ['permits'],
				taskChecklist: [{ id: 'obtained', label: 'Park entry confirmed' }],
			}
			: trek.permit === 'none'
				? {
					objective: 'Confirm no permit office visit is needed',
					why: 'Low-altitude routes often skip Kathmandu permit counters entirely',
					estimatedTime: '10 minutes',
					prerequisites: ['permits'],
					taskChecklist: [{ id: 'obtained', label: 'Confirmed — no permit office visit required' }],
				}
				: {
					objective: 'Collect permits before the trail',
					why: `Checkpoints enforce permit rules. ${formatPermitLines(details)}`,
					estimatedTime: '45 minutes',
					prerequisites: ['permits'],
					taskChecklist: [{ id: 'obtained', label: 'Permits obtained' }],
					documentsRequired: details.documents,
				};

	return {
		preparationTimeline: kind === 'heritage'
			? [
				{ daysBefore: 30, label: 'Book flights', task: 'International flights to Kathmandu', stepId: 'flights' },
				{ daysBefore: 14, label: 'Visa', task: `Apply for Nepal tourist visa (USD ${VISA_FEES_USD[30]})`, stepId: 'visa' },
				{ daysBefore: 7, label: 'Insurance & cash', task: 'Travel insurance + withdraw NPR for site tickets', stepId: 'insurance' },
				{ daysBefore: 3, label: 'Pack & plan route', task: 'Modest clothing, walking shoes, site list', stepId: 'packing' },
				{ daysBefore: 0, label: 'Start walking', task: `Begin at ${trek.trailhead}`, stepId: 'lukla' },
			]
			: kind === 'safari'
				? [
					{ daysBefore: 45, label: 'Book lodge', task: 'Reserve jungle lodge with safari package', stepId: 'flights' },
					{ daysBefore: 30, label: 'Insurance', task: 'Travel medical insurance', stepId: 'insurance' },
					{ daysBefore: 14, label: 'Visa', task: `Nepal tourist visa (USD ${VISA_FEES_USD[30]})`, stepId: 'visa' },
					{ daysBefore: 7, label: 'Confirm park entry', task: 'Verify lodge includes park permit', stepId: 'permits' },
					{ daysBefore: 0, label: 'Safari begins', task: `Travel to ${trek.trailhead}`, stepId: 'lukla' },
				]
				: [
					{ daysBefore: 60, label: 'Book flights & insurance', task: `Flights + insurance to ${trek.insuranceAltitude}m`, stepId: 'flights' },
					{ daysBefore: 30, label: 'Gear and fitness', task: 'Break in boots, check packing list', stepId: 'packing' },
					{ daysBefore: 14, label: 'Visa', task: `Apply for Nepal tourist visa (USD ${VISA_FEES_USD[30]})`, stepId: 'visa' },
					{ daysBefore: 7, label: 'Permits & cash', task: `Obtain ${details.summary}, withdraw NPR`, stepId: 'kathmandu' },
					{ daysBefore: 0, label: 'Trek begins', task: `Travel to ${trek.trailhead}`, stepId: 'lukla' },
				],
		budget: {
			currency: 'USD',
			tiers: [
				{ id: 'budget', label: 'Budget', range: trek.budget.split('–')[0] ? trek.budget : `$${Math.round(mid * 0.7)}`, description: 'Independent / basic teahouse' },
				{ id: 'standard', label: 'Standard', range: trek.budget, description: 'Guide or porter, comfortable pace' },
				{ id: 'comfort', label: 'Comfort', range: `$${Math.round(mid * 1.4)}+`, description: 'Full package with private guide' },
			],
			items: [
				{ id: 'flights-intl', label: 'International flights', budget: 700, standard: 900, comfort: 1200 },
				{ id: 'visa', label: 'Visa (30 days)', budget: 50, standard: 50, comfort: 50 },
				{ id: 'insurance', label: 'Travel insurance', budget: 50, standard: 80, comfort: 120 },
				{ id: 'permits', label: kind === 'heritage' ? 'Site entrance fees' : 'Permits', budget: permitBudget, standard: permitBudget, comfort: permitBudget },
				{ id: 'on-trail', label: 'Food & lodging on trail', budget: Math.round(mid * 0.25), standard: Math.round(mid * 0.35), comfort: Math.round(mid * 0.5) },
				{ id: 'transport', label: 'In-country transport', budget: 30, standard: 80, comfort: 200 },
				{ id: 'guide', label: 'Guide (optional)', budget: 0, standard: 200, comfort: 350 },
				{ id: 'kathmandu', label: 'Kathmandu expenses', budget: 80, standard: 150, comfort: 300 },
			],
		},
		documentsWallet: [
			{ id: 'passport', label: 'Passport', note: 'Valid 6+ months' },
			{ id: 'visa', label: 'Nepal visa', note: '30-day tourist' },
			{ id: 'insurance', label: 'Insurance policy', note: kind === 'heritage' || kind === 'safari' ? 'Medical + evacuation' : `${trek.insuranceAltitude}m altitude cover` },
			{ id: 'flight-intl', label: 'Flight tickets', note: 'Home ↔ Kathmandu' },
			{ id: 'permit', label: kind === 'heritage' ? 'Site entrance fees' : details.summary, note: kind === 'heritage' ? `~NPR ${heritage?.totalBudgetNpr || 0} for main sites` : details.documents.join(', ') || 'Obtain before trail' },
			{ id: 'emergency', label: 'Emergency contacts', note: 'Insurer + Tourist Police 1144' },
		],
		packingChecklist: getPackingForTrek(trek, kind),
		stepGuided: mergeStepGuided({
			dream: {
				objective: `Confirm ${trek.title} fits your goals`,
				why: trek.highlight,
				estimatedTime: '10 minutes',
				taskChecklist: [
					{ id: 'decided', label: `I want to do ${trek.title}` },
					{ id: 'fitness', label: 'I assessed my fitness for this difficulty' },
				],
			},
			research: {
				objective: 'Lock season, duration, and budget',
				why: `${trek.seasons} are the prime windows`,
				estimatedTime: '20 minutes',
				prerequisites: ['dream'],
				taskChecklist: [
					{ id: 'season', label: `Best season: ${trek.seasons}` },
					{ id: 'budget', label: `Typical budget: ${trek.budget}` },
				],
			},
			flights: {
				objective: 'Book travel to Nepal and trailhead',
				why: `Trail starts at ${trek.trailhead}`,
				estimatedTime: '1 hour',
				prerequisites: ['visa'],
				taskChecklist: [{ id: 'booked', label: 'International flights booked' }],
			},
			insurance: kind === 'heritage' || kind === 'safari'
				? {
					objective: 'Standard travel medical insurance',
					why: 'Medical care and evacuation cover — high-altitude trekking cover not required on this route',
					estimatedTime: '30 minutes',
					prerequisites: ['research'],
					taskChecklist: [
						{ id: 'purchased', label: 'Policy purchased' },
						{ id: 'medical', label: 'Medical and evacuation cover verified' },
					],
				}
				: {
					objective: `Insurance to ${trek.insuranceAltitude}m with evacuation`,
					why: 'Helicopter rescue is expensive without cover',
					estimatedTime: '30 minutes',
					prerequisites: ['research'],
					taskChecklist: [
						{ id: 'purchased', label: 'Policy purchased' },
						{ id: 'altitude', label: `Altitude limit ≥ ${trek.insuranceAltitude}m verified` },
					],
				},
			packing: kind === 'heritage'
				? {
					objective: 'Pack for city walking and temple visits',
					why: 'Modest dress and comfortable shoes matter more than trekking gear',
					estimatedTime: '30 minutes',
					usesPackingChecklist: true,
					prerequisites: ['research'],
					taskChecklist: [{ id: 'packed', label: 'Day-walk essentials ready' }],
				}
				: kind === 'safari'
					? {
						objective: 'Pack for jungle safaris',
						why: 'Neutral colours and insect protection for park drives',
						estimatedTime: '30 minutes',
						usesPackingChecklist: true,
						prerequisites: ['research'],
						taskChecklist: [{ id: 'packed', label: 'Safari essentials packed' }],
					}
					: {
						objective: 'Complete packing checklist',
						why: 'Limited shops on remote trails',
						estimatedTime: '1–2 hours',
						usesPackingChecklist: true,
						prerequisites: ['research'],
						taskChecklist: [{ id: 'packed', label: 'Packing checklist complete' }],
					},
			permits: permitGuided,
			'permit-offices': permitOfficeGuided,
			lukla: {
				objective: `Reach ${trek.trailhead}`,
				why: `Access mode: ${trek.access}`,
				estimatedTime: '1 day',
				prerequisites: ['kathmandu'],
				taskChecklist: [{ id: 'arrived', label: `Arrived at ${trek.trailhead}` }],
			},
			trek: {
				objective: `Complete ${trek.title}`,
				why: trek.highlight,
				estimatedTime: trek.duration,
				prerequisites: ['lukla'],
				taskChecklist: [{ id: 'completed', label: 'Trek completed safely' }],
			},
		}),
	};
}

function buildDeepContent(trek) {
	const kind = getJourneyKind(trek);
	const details = getPermitDetails(trek.permit);
	const offices = details.offices.length > 0 ? details.offices : [PERMIT_OFFICES.ntbKtm];

	return {
		officeLocations: offices,
		officialPdfs: [
			{ label: 'NTB — Plan your trip', url: OFFICIAL_SOURCES.ntb.url, source: OFFICIAL_SOURCES.ntb },
			{ label: 'Department of Immigration — Visa', url: OFFICIAL_SOURCES.immigration.url, source: OFFICIAL_SOURCES.immigration },
			...(details.source === OFFICIAL_SOURCES.ntnc ? [{ label: 'NTNC e-permit (ACAP/MCAP)', url: 'https://epermit.ntnc.org.np/', source: OFFICIAL_SOURCES.ntnc }] : []),
			...(details.source === OFFICIAL_SOURCES.dnp ? [{ label: 'DNPWC — Park entry fees', url: OFFICIAL_SOURCES.ntbParkFees.url, source: OFFICIAL_SOURCES.dnp }] : []),
		],
		weatherByMonth: getWeatherForTrek(trek),
		connectivity: {
			lastVerified: LAST_VERIFIED,
			sim: [{ provider: 'Ncell', recommendation: 'Buy in Thamel on arrival', trailSignal: 'Varies by region — assume weak on trail' }],
			wifi: { kathmandu: 'Hotels and cafés', trail: 'Limited — do not rely on WiFi' },
		},
		altitudeSickness: kind === 'heritage' || kind === 'safari'
			? {
				source: OFFICIAL_SOURCES.hra,
				lastVerified: LAST_VERIFIED,
				goldenRules: ['Save Tourist Police 1144 offline', 'Know your nearest hospital or lodge contact'],
				symptoms: { mild: 'Not applicable on this low-altitude route', severe: 'Seek medical care for any serious illness or injury' },
				prevention: [`Max altitude on this journey: ${trek.maxAltitude}`, 'Stay hydrated', 'Use registered guides and lodges'],
				aidPosts: [],
			}
			: {
				source: OFFICIAL_SOURCES.hra,
				lastVerified: LAST_VERIFIED,
				goldenRules: ['Never ascend with worsening symptoms', 'Descend if symptoms become severe'],
				symptoms: { mild: 'Headache, nausea above 3,000m', severe: 'Ataxia, confusion — descend immediately' },
				prevention: ['Gradual ascent', 'Hydration', `Max altitude on this trek: ${trek.maxAltitude}`],
				aidPosts: [],
			},
		emergencyContacts: [
			{ label: 'Tourist Police', number: '1144', note: 'Toll-free, English, 24/7', official: true },
			{ label: 'Police', number: '100', note: 'General emergency', official: true },
		],
		helicopterEvacuation: {
			lastVerified: LAST_VERIFIED,
			costWithoutInsurance: 'USD 3,000–15,000+',
			procedure: ['Call insurer first', 'Tourist Police 1144 for coordination'],
			touristPolice: '1144',
			insuranceTip: 'Save your insurer emergency number offline before you leave Kathmandu.',
		},
		porterGuide: {
			lastVerified: LAST_VERIFIED,
			hiring: 'Hire through a TAAN-registered trekking agency.',
			costPerDay: 'USD 15–30/day depending on region',
			weightLimit: '15 kg per porter',
			tipping: '10–15% at trek end',
			etiquette: ['Confirm porter insurance with your agency before departure', 'Carry your own daypack — porter takes duffel only'],
		},
		licensedAgencies: {
			verifyUrl: 'https://www.taan.org.np/',
			verifyLabel: 'TAAN',
			howToVerify: ['Verify TAAN membership before paying'],
			redFlags: ['Airport touts', '100% upfront cash'],
			agencies: [],
		},
		gearRentalShops: [{ name: 'Thamel gear shops', area: 'Kathmandu', items: 'Boots, jackets, sleeping bags', rates: '$2–3/day' }],
		gpxMap: {
			description: `${trek.title} — verify route on OpenStreetMap before travel.`,
			resources: [{ label: 'OpenStreetMap — Nepal', url: 'https://www.openstreetmap.org/#map=8/28.394/84.124', type: 'interactive_map' }],
		},
		offlineDownloads: [
			{ id: 'packing', label: 'Packing checklist', type: 'checklist' },
			{ id: 'documents', label: 'Documents wallet', type: 'checklist' },
		],
		travelerFaqs: [
			{ q: `How hard is ${trek.title}?`, a: `${trek.difficulty}. Duration ${trek.duration}. Max altitude ${trek.maxAltitude}.`, category: 'planning' },
			{ q: 'What permits do I need?', a: kind === 'heritage' && getHeritageFees(trek.slug)
				? getHeritageFees(trek.slug).summary
				: formatPermitLines(details), category: 'regulations' },
			{ q: 'Do I need a guide?', a: trek.restricted ? 'Yes — restricted area requires licensed guide.' : kind === 'heritage' ? 'Optional — a local cultural guide helps with temple etiquette.' : 'Often optional — verify current NTB rules for your route.', category: 'regulations' },
			{ q: 'When is the best season?', a: trek.seasons, category: 'season' },
		],
	};
}

/** Build a complete guided journey from a catalog entry */
export function buildJourneyFromCatalog(trek) {
	const kind = getJourneyKind(trek);
	const base = {
		slug: trek.slug,
		title: `${trek.title} Journey`,
		eyebrow: 'Guided Journey',
		subtitle: trek.highlight,
		lead: kind === 'heritage'
			? `${trek.highlight} Guided step-by-step from visa to heritage sites.`
			: kind === 'safari'
				? `${trek.highlight} Guided step-by-step from visa to safari.`
				: `${trek.highlight} Guided step-by-step from visa to trail.`,
		hero: heroFor(trek.imageKey),
		lastVerified: LAST_VERIFIED,
		coverage: Math.max(trek.coverage, 68),
		status: trek.coverage >= 70 ? 'complete' : 'partial',
		destination: { slug: trek.slug, name: trek.title },
		district: { slug: trek.district.toLowerCase().replace(/\s+/g, '-').split('/')[0].trim(), name: trek.district },
		region: trek.region,
		quickFacts: [
			{ value: trek.duration, label: 'Duration' },
			{ value: trek.maxAltitude, label: 'Max altitude' },
			{ value: trek.seasons, label: 'Best seasons' },
			{ value: trek.difficulty, label: 'Difficulty' },
			{ value: trek.budget, label: 'Budget (excl. flights)' },
		],
		primarySource: getPermitDetails(trek.permit).source || OFFICIAL_SOURCES.ntb,
		officialSources: [OFFICIAL_SOURCES.ntb, OFFICIAL_SOURCES.immigration, OFFICIAL_SOURCES.touristPolice],
		faqs: buildDeepContent(trek).travelerFaqs.map((f) => ({ q: f.q, a: f.a })),
		related: [
			{ label: 'All guided journeys', to: '/journeys' },
			{ label: 'Trekking in Nepal', to: '/trekking' },
			{ label: 'Everest Base Camp', to: '/journeys/everest-base-camp' },
		],
		steps: buildSteps(trek),
		trekDays: buildTrekDays(trek),
	};

	return createJourney(base, buildGuidedMeta(trek), buildDeepContent(trek), {
		coverage: Math.max(trek.coverage, 68),
		status: trek.coverage >= 70 ? 'complete' : 'partial',
	});
}

/** Generate all catalog journeys (excludes hand-crafted slugs) */
export function generateCatalogJourneys(catalog, handcraftedSlugs) {
	return catalog
		.filter((t) => !handcraftedSlugs.has(t.slug))
		.map((t) => buildJourneyFromCatalog(t));
}
